import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

const GV_FILTER_CONFIG = {
  gender: [
    { label: 'ALL', value: '' },
    { label: 'MEN', value: 'herren' },
    { label: 'WOMEN', value: 'damen' }
  ],
  type: [
    { label: 'TOPS', handle: 'tops' },
    { label: 'LAYERS', handle: 'layers' },
    { label: 'PANTS', handle: 'pants' },
    { label: 'SHORTS', handle: 'shorts' },
    { label: 'SWIMWEAR', handle: 'swimwear' }

  ],
  lines: {
    'golden-vein': [
      { label: 'CORE', base: 'core' },
      { label: 'FORM', base: 'form' },
      { label: 'LEGACY', base: 'archive' }
    ],
    pulse: [
      { label: 'GENESIS', base: 'pulse-core' },
      { label: 'SPECTRE', base: 'spectre' }
    ]
  }
};

const GV_TYPE_HANDLES = GV_FILTER_CONFIG.type.map((item) => item.handle);
const GV_GENDER_HANDLES = GV_FILTER_CONFIG.gender.map((item) => item.value).filter(Boolean);
const GV_GOLDEN_VEIN_BASES = ['golden-vein', 'core', 'form', 'archive'];
const GV_PULSE_BASES = ['pulse', 'pulse-core', 'spectre'];
const GV_LEGACY_COLLECTIONS = {
  'herren-tops': { base: 'herren', type: 'tops' },
  'herren-layers': { base: 'herren', type: 'layers' },
  'herren-pants': { base: 'herren', type: 'pants' },
  'herren-shorts': { base: 'herren', type: 'shorts' },
  'herren-swimwear': { base: 'herren', type: 'swimwear' },
  'damen-tops': { base: 'damen', type: 'tops' },
  'damen-layers': { base: 'damen', type: 'layers' },
  'damen-pants': { base: 'damen', type: 'pants' },
  'damen-shorts': { base: 'damen', type: 'shorts' },
  'damen-swimwear': { base: 'damen', type: 'swimwear' }
};
let gvFilterLoading = false;

function gvPathState() {
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (parts[0] !== 'collections' || !parts[1]) return null;
  const rawBase = parts[1].toLowerCase();
  const legacy = GV_LEGACY_COLLECTIONS[rawBase];
  const base = legacy ? legacy.base : rawBase;
  const tags = (parts[2] || '').split('+').filter(Boolean).map((tag) => decodeURIComponent(tag).toLowerCase());
  if (legacy && !tags.includes(legacy.type)) tags.push(legacy.type);

  const brand = GV_GOLDEN_VEIN_BASES.includes(rawBase)
    ? 'golden-vein'
    : GV_PULSE_BASES.includes(rawBase)
      ? 'pulse'
      : '';
  const gender = base === 'herren' || base === 'damen'
    ? base
    : GV_GENDER_HANDLES.find((handle) => tags.includes(handle)) || '';
  const type = GV_TYPE_HANDLES.find((handle) => tags.includes(handle)) || '';

  return { rawBase, base, tags, brand, gender, type };
}

function gvBuildFilterGroup(name, items) {
  const group = document.createElement('div');
  group.className = 'gv-live-filters__group gv-live-filters__group--' + name;
  group.dataset.group = name;

  items.forEach((item) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'gv-live-filters__button';
    button.textContent = item.label;
    button.dataset.filterGroup = name;
    if (item.base) button.dataset.filterBase = item.base;
    if (Object.hasOwn(item, 'value')) button.dataset.filterValue = item.value;
    if (item.handle) button.dataset.filterHandle = item.handle;
    if (item.disabled) {
      button.disabled = true;
      button.title = 'Coming soon';
    }
    group.appendChild(button);
  });

  return group;
}

function gvBuildLineGroup(brand) {
  const group = gvBuildFilterGroup('line', GV_FILTER_CONFIG.lines[brand] || []);
  const label = document.createElement('span');
  label.className = 'gv-live-filters__label';
  label.textContent = 'LINES';
  group.prepend(label);
  return group;
}

function gvInjectFilterBar() {
  const state = gvPathState();
  const list = document.querySelector('results-list');
  if (!state || !list || list.querySelector('.gv-live-filters')) return;

  const bar = document.createElement('nav');
  bar.className = 'gv-live-filters';
  bar.setAttribute('aria-label', 'Product navigation');

  if (state.brand) bar.appendChild(gvBuildLineGroup(state.brand));
  bar.appendChild(gvBuildFilterGroup('gender', GV_FILTER_CONFIG.gender));
  bar.appendChild(gvBuildFilterGroup('type', GV_FILTER_CONFIG.type));

  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'gv-live-filters__reset';
  reset.textContent = 'RESET';
  reset.dataset.resetFilters = 'true';
  bar.appendChild(reset);

  bar.addEventListener('click', (event) => {
    const button = event.target.closest('.gv-live-filters__button, .gv-live-filters__reset');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    const url = gvTargetUrl(button, button.dataset.resetFilters === 'true');
    if (!url) return;

    const currentState = gvPathState();
    const targetParts = url.pathname.split('/').filter(Boolean);
    const targetBase = targetParts[0] === 'collections' ? targetParts[1] : '';

    if (currentState && targetBase && targetBase !== currentState.rawBase) {
      window.location.assign(url.toString());
      return;
    }

    gvLoadFilters(url);
  }, true);

  const wrapper = list.querySelector('.collection-wrapper');
  if (wrapper) list.insertBefore(bar, wrapper);
  gvUpdateButtons();
}

function gvUpdateButtons() {
  const state = gvPathState();
  if (!state) return;

  document.querySelectorAll('.gv-live-filters__button').forEach((button) => {
    const group = button.dataset.filterGroup;
    const base = button.dataset.filterBase || '';
    const value = button.dataset.filterValue || '';
    const handle = button.dataset.filterHandle || '';
    let active = false;
    if (group === 'gender') active = state.gender === value;
    if (group === 'type') active = state.type === handle;
    if (group === 'line') active = state.rawBase === base;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  const reset = document.querySelector('.gv-live-filters__reset');
  if (reset) reset.classList.toggle('is-visible', state.rawBase !== 'all' || state.tags.length > 0);
}

function gvDimensionTags(state, gender = state.gender, type = state.type) {
  return [gender, type].filter(Boolean);
}

function gvTargetUrl(button, resetAll) {
  const state = gvPathState();
  if (!state) return null;

  const targetBase = button.dataset.filterBase;
  const group = button.dataset.filterGroup;
  const value = button.dataset.filterValue || '';
  const handle = button.dataset.filterHandle;
  const url = new URL(window.location.href);
  let base = state.base;
  let tags = gvDimensionTags(state);

  if (resetAll) {
    base = state.brand ? state.rawBase : 'all';
    tags = [];
  } else if (group === 'gender') {
    base = state.brand ? state.rawBase : value || 'all';
    tags = state.brand ? gvDimensionTags(state, value, state.type) : [state.type].filter(Boolean);
  } else if (group === 'type' && handle) {
    tags = gvDimensionTags(state, state.gender, handle);
    if (!state.brand && (state.base === 'herren' || state.base === 'damen')) {
      tags = [handle];
    }
  } else if (group === 'line' && targetBase) {
    base = targetBase;
    tags = gvDimensionTags(state);
  }

  url.pathname = '/collections/' + base + (tags.length ? '/' + tags.join('+') : '');
  url.search = '';
  return url;
}

async function gvLoadFilters(url, pushState = true) {
  if (!url || gvFilterLoading) return;
  const list = document.querySelector('results-list');
  const sectionId = list && list.getAttribute('section-id');
  if (!list || !sectionId) return;

  gvFilterLoading = true;
  document.documentElement.classList.add('gv-filter-loading');

  try {
    const requestUrl = new URL(url.toString());
    requestUrl.searchParams.set('section_id', sectionId);
    const response = await fetch(requestUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Filter request failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextList = doc.querySelector('results-list');
    if (!nextList) throw new Error('Product list missing');

    list.replaceWith(nextList);
    if (pushState) history.pushState({ gvFilters: true }, '', url.toString());
    gvInjectFilterBar();
    nextList.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (error) {
    window.location.assign(url.toString());
  } finally {
    gvFilterLoading = false;
    document.documentElement.classList.remove('gv-filter-loading');
  }
}

window.addEventListener('popstate', () => {
  const state = gvPathState();
  if (state) gvLoadFilters(new URL(window.location.href), false);
});

const gvFilterStyle = document.createElement('style');
gvFilterStyle.textContent = [
  '.gv-live-filters{grid-column:1/-1;width:100%;box-sizing:border-box;display:flex;align-items:center;gap:0;padding:10px var(--page-margin);border-block:1px solid rgb(var(--color-foreground-rgb)/.12);background:var(--color-background);position:relative;z-index:2;overflow-x:auto;scrollbar-width:none;-webkit-overflow-scrolling:touch}',
  '.gv-live-filters::-webkit-scrollbar,.gv-live-filters__group::-webkit-scrollbar{display:none}',
  '.gv-live-filters__group{display:flex;flex:0 0 auto;align-items:center;gap:6px}',
  '.gv-live-filters__group--gender,.gv-live-filters__group--type{border-inline-start:1px solid rgb(var(--color-foreground-rgb)/.24);margin-inline-start:14px;padding-inline-start:14px}',
  '.gv-live-filters__label{font-size:.72rem;font-weight:650;letter-spacing:.14em;white-space:nowrap;padding:9px 11px 9px 0}',
  '.gv-live-filters__button,.gv-live-filters__reset{appearance:none;border:0;background:transparent;color:var(--color-foreground);font:inherit;font-size:.72rem;letter-spacing:.12em;white-space:nowrap;padding:9px 11px;cursor:pointer;text-decoration:none}',
  '.gv-live-filters__button{border:1px solid transparent}',
  '.gv-live-filters__button:hover,.gv-live-filters__button.is-active{border-color:currentColor;text-decoration:none}',
  '.gv-live-filters__button:disabled{opacity:.35;cursor:not-allowed}',
  '.gv-live-filters__reset{display:none;opacity:.65;margin-inline-start:auto}',
  '.gv-live-filters__reset.is-visible{display:block}',
  '.gv-filter-loading results-list{opacity:.55;pointer-events:none;transition:opacity .18s ease}',
  '@media(max-width:989px){.gv-live-filters{padding:9px 16px}.gv-live-filters__group--gender,.gv-live-filters__group--type{margin-inline-start:10px;padding-inline-start:10px}.gv-live-filters__label,.gv-live-filters__button,.gv-live-filters__reset{font-size:.66rem;padding-block:8px}.gv-live-filters__reset.is-visible{margin-inline-start:10px}}'
].join('');
document.head.appendChild(gvFilterStyle);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', gvInjectFilterBar, { once: true });
} else {
  gvInjectFilterBar();
}
document.addEventListener('shopify:section:load', gvInjectFilterBar);
